import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { memo, type ReactNode } from 'react';

import { SortableItemContext } from '../context';
import './SortableItem.css';

export interface SortableItemProps {
  /** Sortable identity — must match an id in the owning `SortableContext`. */
  id: string | number;
  /**
   * Any content — an `Accordion`, a row, a card. `SortableItem` has no
   * opinion on what it wraps; it renders `children` as-is inside the node
   * that dnd-kit measures and animates.
   */
  children: ReactNode;
  /** Disables dragging for this item only. @default false */
  disabled?: boolean;
}

/**
 * Internal to `SortableList` — wraps each item to make it sortable within
 * the `DndContext` + `SortableContext` that `SortableList` owns. It provides
 * the activator wiring (`attributes`, `listeners`, `setActivatorNodeRef`) via
 * context so a `SortableHandle` rendered anywhere inside — e.g. as an
 * `Accordion` `leadingSlot` — picks it up automatically with no prop
 * plumbing.
 *
 * While a drag is in progress, the item currently under the pointer shows a
 * thin edge divider indicating which side the dragged item would land on —
 * computed from `useSortable`'s own `isOver`/`index`/`activeIndex`, with no
 * list-level state needed. Deliberately no elevation/shadow on the dragged
 * item itself — the divider is the only "in progress" affordance.
 */
function SortableItemComponent({
  id,
  children,
  disabled = false,
}: SortableItemProps) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    index,
    activeIndex,
    isOver,
  } = useSortable({ id, disabled });

  // A dragged item moving down the list (activeIndex < index) will land
  // after this item — show the divider below. Moving up (activeIndex >
  // index) lands before it — show the divider above. Hovering its own
  // original slot (activeIndex === index) shows neither.
  const showDropLineAbove = isOver && activeIndex > index;
  const showDropLineBelow = isOver && activeIndex < index;

  return (
    <SortableItemContext.Provider
      value={{ attributes, isDragging, listeners, setActivatorNodeRef }}
    >
      <div
        ref={setNodeRef}
        className={clsx(
          'eds-sortable-item',
          showDropLineAbove && 'eds-sortable-item--drop-line-above',
          showDropLineBelow && 'eds-sortable-item--drop-line-below',
        )}
        style={{ transform: CSS.Transform.toString(transform), transition }}
      >
        {children}
      </div>
    </SortableItemContext.Provider>
  );
}

export const SortableItem = memo(SortableItemComponent);

SortableItem.displayName = 'SortableItem';
